import { NativeBiometric, BiometryType } from 'capacitor-native-biometric';
import { isNative } from './platform';
import { STORAGE_KEYS, getItem, setItem } from './storage';

const CRED_SERVER = 'gpl.mobile';
const CRED_USERNAME_FIELD = 'biometric-marker';

export interface BiometryInfo {
  available: boolean;
  /** Human-facing name: "Face ID" / "Fingerprint" / "Biometric" */
  kind: string;
}

export async function inspectBiometric(): Promise<BiometryInfo> {
  if (!isNative()) return { available: false, kind: 'Biometric' };
  try {
    const res = await NativeBiometric.isAvailable();
    if (!res.isAvailable) return { available: false, kind: 'Biometric' };
    const kind =
      res.biometryType === BiometryType.FACE_ID ? 'Face ID' :
      res.biometryType === BiometryType.TOUCH_ID ? 'Touch ID' :
      res.biometryType === BiometryType.FACE_AUTHENTICATION ? 'Face Unlock' :
      res.biometryType === BiometryType.FINGERPRINT ? 'Fingerprint' :
      'Biometric';
    return { available: true, kind };
  } catch {
    return { available: false, kind: 'Biometric' };
  }
}

export async function isBiometricEnabled(): Promise<boolean> {
  try {
    const val = await getItem(STORAGE_KEYS.BIOMETRIC_ENABLED);
    return val === '1';
  } catch {
    return false;
  }
}

/**
 * Enable biometric lock: verify once, store an opaque marker so that
 * subsequent boots can re-verify against the same biometry. Uses the
 * Keychain / Keystore backing the plugin — never writes raw credentials.
 */
export async function enableBiometric(): Promise<void> {
  if (!isNative()) throw new Error('Biometric only available on native platforms');
  await NativeBiometric.verifyIdentity({
    reason: 'Enable biometric lock for GPL Pro',
    title: 'GPL Pro',
  });
  await NativeBiometric.setCredentials({
    username: CRED_USERNAME_FIELD,
    password: crypto.randomUUID(),
    server: CRED_SERVER,
  });
  await setItem(STORAGE_KEYS.BIOMETRIC_ENABLED, '1');
}

export async function disableBiometric(): Promise<void> {
  if (isNative()) {
    try {
      await NativeBiometric.deleteCredentials({ server: CRED_SERVER });
    } catch {
      void 0;
    }
  }
  await setItem(STORAGE_KEYS.BIOMETRIC_ENABLED, '0');
}

/**
 * Prompt the user to verify with biometry. Resolves on success, throws on
 * cancel/fail. Call this on app launch when `isBiometricEnabled()` is true.
 */
export async function verifyBiometric(reason: string): Promise<void> {
  if (!isNative()) throw new Error('Biometric only available on native platforms');
  await NativeBiometric.verifyIdentity({
    reason,
    title: 'GPL Pro',
    negativeButtonText: 'Cancel',
  });
}
